import type { Item, ItemList } from "@rubi/api-generated/clientRs/types.gen";

export type ParseItemListResult =
  | { ok: true; itemList: ItemList }
  | { ok: false; error: string };

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && !!value.trim();

const checkCategory = (value: unknown, field: string): string | null => {
  if (value === undefined || value === null) return null;
  if (
    !isPlainObject(value) ||
    !isNonEmptyString(value.name) ||
    !isNonEmptyString(value.code)
  ) {
    return `"${field}" must be an object with non-empty string "name" and "code"`;
  }
  return null;
};

const checkItem = (item: unknown): string | null => {
  if (!isPlainObject(item)) return "must be an object";
  if (typeof item.label !== "string" || !item.label.trim()) {
    return 'is missing a "label"';
  }

  const { value } = item;
  if (isPlainObject(value)) {
    if (!isNonEmptyString(value.code)) {
      return 'has a "value" without a non-empty "code"';
    }
    for (const field of ["category1", "category2", "category3"]) {
      const error = checkCategory(value[field], field);
      if (error) return error;
    }
  } else if (!isNonEmptyString(value)) {
    return 'needs a "value" that is a non-empty code string or an object with a non-empty "code"';
  }

  if (item.translations !== undefined) {
    if (
      !isPlainObject(item.translations) ||
      Object.values(item.translations).some((t) => typeof t !== "string")
    ) {
      return 'has "translations" that are not a map of language to text';
    }
  }
  return null;
};

export const parseItemListJson = (raw: string): ParseItemListResult => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return {
      ok: false,
      error: `Invalid JSON: ${err instanceof Error ? err.message : String(err)}`,
    };
  }

  if (Array.isArray(parsed)) {
    return {
      ok: false,
      error:
        'This looks like a v1 list (a bare array). Expected { "version": 2, "items": [...] }.',
    };
  }
  if (!isPlainObject(parsed)) {
    return {
      ok: false,
      error: 'Expected an object like { "version": 2, "items": [...] }.',
    };
  }
  if (parsed.version !== undefined && parsed.version !== 2) {
    return {
      ok: false,
      error: `Unsupported version ${JSON.stringify(parsed.version)}; only version 2 can be imported.`,
    };
  }
  if (!Array.isArray(parsed.items) || parsed.items.length === 0) {
    return { ok: false, error: '"items" must be a non-empty array.' };
  }

  for (const [index, item] of parsed.items.entries()) {
    const error = checkItem(item);
    if (error) return { ok: false, error: `Item ${index + 1} ${error}.` };
  }

  return {
    ok: true,
    itemList: { version: 2, items: parsed.items as Item[] },
  };
};
